import { useMemo, useCallback, useState } from 'react';
import type { MouseEvent } from 'react';
import { DownloadIcon, Info } from 'lucide-react';
import { getWidgetStores, InfoMenu, InfoMenuInput, InfoMenuType } from '@/entities/widget';
import { Html5Player } from './components/Html5Player';
import type { VideoWidgetProps } from '../model/video-widget.types';
import { formatBytes, formatDateTime } from '../lib/video-widget.utils';
import { normalizeVersions } from '../lib/video-widget.normalize';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { useSaveToProjectAction } from '@/shared/ui';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { downloadFile } from '@/shared/utils/downloadFile';
import { logger } from '@/lib/utils/logger';
import { useTranslation } from 'react-i18next';

export const VideoWidget = (props: VideoWidgetProps) => {
  const { t } = useTranslation('contentTest');

  const data = useMemo(() => normalizeVersions(props.data), [props.data]);

  const video = data.video;
  const title =
    data.title || video?.metadata?.fileName || t('widget.useWidgetVersions.generatedVideo');

  const { downloadUrl, isLoading, refetch } = useGetDownloadUrl(video?.assetId);

  const [infoMenuAnchor, setInfoMenuAnchor] = useState<HTMLElement | null>(null);

  // Version selector support — store access via the widget binding seam
  // . See `entities/widget/lib/widget-store-bindings`.
  const widgetStoreBindings = getWidgetStores().widgetStore;
  const { widget } = widgetStoreBindings.useWidgetPanel();
  const agentId = widget?.agentName ?? '';
  const versionConfig = widgetStoreBindings.useWidgetVersions(agentId);

  // "Save to project" — the shared hook handles gating (flag + workspace + live
  // source artifact) and returns null when the action shouldn't render.
  const sourceArtifactId = typeof widget?.artifactId === 'string' ? widget.artifactId : undefined;
  // eslint-disable-next-line i18next/no-literal-string -- artifact kind wire enum value
  const saveAction = useSaveToProjectAction('video', sourceArtifactId);

  const handleInfoMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setInfoMenuAnchor(event.currentTarget);
  };

  const handleInfoMenuClose = () => {
    setInfoMenuAnchor(null);
  };

  // Build info popover rows
  const infoSettings = useMemo(() => {
    const rows: InfoMenuInput = [];

    if (data.input?.files && data.input.files.length > 0) {
      rows.push({
        [t('videoWidget.infoMenu.referenceImages')]: {
          type: InfoMenuType.Image,
          value: data.input.files,
        },
      });
    }

    const when = formatDateTime(data.createdAt);
    if (when)
      rows.push({ [t('videoWidget.infoMenu.date')]: { type: InfoMenuType.Text, value: when } });

    const model = data.input?.settings?.model || '—';
    rows.push({ [t('videoWidget.infoMenu.model')]: { type: InfoMenuType.Text, value: model } });

    if (data.input?.prompt)
      rows.push({
        [t('videoWidget.infoMenu.prompt')]: { type: InfoMenuType.Text, value: data.input.prompt },
      });

    if (video?.metadata?.fileName)
      rows.push({
        [t('videoWidget.infoMenu.fileName')]: {
          type: InfoMenuType.Text,
          value: video.metadata.fileName,
        },
      });
    if (video?.mimeType)
      rows.push({
        [t('videoWidget.infoMenu.mimeType')]: { type: InfoMenuType.Text, value: video.mimeType },
      });
    rows.push({
      [t('videoWidget.infoMenu.size')]: {
        type: InfoMenuType.Text,
        value: formatBytes(video?.metadata?.size),
      },
    });

    return rows;
  }, [
    data.createdAt,
    data.input,
    video?.metadata?.fileName,
    video?.metadata?.size,
    video?.mimeType,
    t,
  ]);

  const handleDownload = useCallback(() => {
    if (!downloadUrl && !video?.assetId) return;

    void (async () => {
      if (!downloadUrl && video?.assetId) await refetch();

      if (!downloadUrl) return;

      try {
        await downloadFile({
          url: downloadUrl,
          mimeType: video?.mimeType,
          fileName: video?.metadata?.fileName,
        });
      } catch (e) {
        logger.error('Video download error:', e);
      }
    })();
  }, [downloadUrl, video?.assetId, video?.mimeType, video?.metadata?.fileName, refetch]);

  if (!downloadUrl || isLoading) {
    return (
      <SidePanel
        title={title}
        headerConfig={{
          chip: {
            label: t('videoWidget.video'),
            color: 'lightBlue',
          },
          versionConfig,
          actions: [
            {
              type: 'icon',
              icon: <Info size={20} />,
              onClick: handleInfoMenuOpen,
              tooltip: t('videoWidget.viewPromptInfo'),
            },
          ],
        }}
      >
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full p-4">
            <p className="mt-12 text-muted-foreground">{t('videoWidget.noVideoUrlProvided')}</p>
          </div>
        </div>

        <InfoMenu
          input={infoSettings}
          anchorEl={infoMenuAnchor}
          open={Boolean(infoMenuAnchor)}
          onClose={handleInfoMenuClose}
        />
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={title}
      headerConfig={{
        chip: {
          label: t('videoWidget.video'),
          color: 'lightBlue',
        },
        versionConfig,
        actions: [
          {
            type: 'icon',
            icon: <Info size={20} />,
            onClick: handleInfoMenuOpen,
            tooltip: t('videoWidget.viewPromptInfo'),
          },
          {
            type: 'icon',
            icon: <DownloadIcon />,
            onClick: handleDownload,
            tooltip: t('videoWidget.downloadVideo'),
          },
          ...(saveAction ? [saveAction] : []),
        ],
      }}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full p-4">
          <div className="mt-6 relative">
            {downloadUrl && (
              <Html5Player key={downloadUrl} src={downloadUrl} type={video?.mimeType} />
            )}
          </div>
        </div>
      </div>

      <InfoMenu
        input={infoSettings}
        anchorEl={infoMenuAnchor}
        open={Boolean(infoMenuAnchor)}
        onClose={handleInfoMenuClose}
      />
    </SidePanel>
  );
};
